import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { UseOutletContextContact } from './Contact';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { ApiDigitalAdminContact } from '../../../services/admin/contact.service';
import { Error } from '../../../models/error.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { Contact } from '../../../models/contact.model';
import { getError } from '../../../languages/translations/response';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';

import AppContactFormStepper from './component/ContactFormStepper';
import AppPreloader from '../../../components/Preloader';

export interface ContactCreateProps {};

let errorResponse: Error, contactResponse: Contact;

const ContactCreatePage: React.FunctionComponent<ContactCreateProps> = props => {
  const {setRoute, appServiceForUser, companyForUser, meCompanies, settingsForCompany, loadAllContacts, loadContactsForCompany, loadCompanyForUser} = UseOutletContextContact()
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [uuid, setUuid] = useState({value: '', valid: true})
  const [type, setType] = useState({value: '', valid: false})
  const [name, setName] = useState({value: '', valid: false})
  const [firstName, setFirstName] = useState({value: '', valid: false})
  const [lastName, setLastName] = useState({value: '', valid: false})
  const [logo, setLogo] = useState({value: {name: '', size: 0, type: '', view: '', file: null as any}, valid: {file: true, message: ''}})
  const [email, setEmail] = useState({value: '', valid: true})
  const [phone, setPhone] = useState({value: '', valid: true})
  const [website, setWebsite] = useState({value: '', valid: true})
  const [registerNumber, setRegisterNumber] = useState({value: '', valid: true})
  const [vatNumber, setVatNumber] = useState({value: '', valid: true})
  const [street, setStreet] = useState({value: '', valid: true})
  const [streetNumber, setStreetNumber] = useState({value: '', valid: true})
  const [additional, setAdditional] = useState({value: '', valid: true})
  const [administrativeArea, setAdministrativeArea] = useState({value: '', valid: true})
  const [city, setCity] = useState({value: '', valid: true})
  const [zipCode, setZipCode] = useState({value: '', valid: true})
  const [country, setCountry] = useState({value: '', valid: true})
  const [currency, setCurrency] = useState({value: '', valid: true, object: null as any})
  const [financialEntity, setFinancialEntity] = useState({value: '', valid: true})
  const [iban, setIban] = useState({value: '', valid: true})
  const [bban, setBban] = useState({value: '', valid: true})
  const [bic, setBic] = useState({value: '', valid: true})
  const [language, setLanguage] = useState({value: '', valid: true})
  const [qrType, setQrType] = useState({value: '', valid: true})
  const [companies, setCompanies] = useState<string[]>([])

  const submitContactCreate = async () => {
    setLoadIndicator('on')

    if (companyForUser) {
      let addressStreetNumber = (streetNumber.value.length === 0) ? 'NN' : streetNumber.value
      let valueIban = iban.value.toUpperCase().trim().replaceAll(' ', '')
      let valueBban = bban.value.toUpperCase().trim().replaceAll(' ', '')
      let valueBic = bic.value.toUpperCase().trim().replaceAll(' ', '')
      let cantonForBackend = administrativeArea.value;

      await ApiDigitalAdminContact.postContact(companyForUser.id, type.value, uuid.value, name.value, firstName.value, lastName.value, logo.value.file, email.value, phone.value, website.value, registerNumber.value, vatNumber.value, street.value, addressStreetNumber, additional.value, cantonForBackend, city.value, zipCode.value, country.value, currency.value, financialEntity.value, valueIban, valueBban, valueBic, language.value, qrType.value, companies).then( (response) => {
        if (response.status === 201) {
          contactResponse = response.data
          loadAllContacts(contactResponse.id_company)
          loadContactsForCompany(contactResponse.id_company)

          const uuidProvided = uuid.value.trim().length > 0
          const isUser = contactResponse.is_user

          GlobalSwal.fire({
            title: lang.labels.successfullyCreatedContact,
            text: uuidProvided && !isUser ? lang.labels.uuidNotLinked : lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: uuidProvided && !isUser ? 3500 : 2200
          }).then( () => {
            navigate('/main/contact/list', {replace: true})
          })
        } else {
          errorResponse = response.data

          const errorTitle = errorResponse.code === 'VALIDATION_ERROR' && errorResponse.message?.toLowerCase().includes('type mismatch')
            ? lang.labels.uuidTypeMismatch
            : getError(errorResponse.code, lang.code, errorResponse.message)

          GlobalSwal.fire({
            title: errorTitle,
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        setLoadIndicator('off')
      })
    }
  }

  const prefillFromCompany = (c: any) => {
    if (!c) return
    if (c.currency && !currency.value) setCurrency({value: c.currency.code ?? c.currency, valid: true, object: c.currency ?? null})
    if (c.language && !language.value) setLanguage({value: c.language, valid: true})
  }

  useEffect( () => {
    if (!companyForUser) return
    const c: any = companyForUser

    if (c.currency || c.language) {
      prefillFromCompany(c)
      return
    }

    if (appServiceForUser?.api_key && loadCompanyForUser) {
      loadCompanyForUser(appServiceForUser.api_key, companyForUser.id, (full: any) => {
        prefillFromCompany(full)
      })
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    if (settingsForCompany?.qr_type && !qrType.value) {
      setQrType({value: settingsForCompany.qr_type, valid: true})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsForCompany])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.contacts, branch: lang.labels.createContact}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { appServiceForUser && companyForUser && meCompanies
        ?
        <div className="card">
          <div className="card-body">
            <AppContactFormStepper loadIndicator={loadIndicator} uuid={uuid} type={type} name={name} firstName={firstName} lastName={lastName} logo={logo} email={email} phone={phone} website={website} vatNumber={vatNumber} registerNumber={registerNumber} street={street} streetNumber={streetNumber} additional={additional} administrativeArea={administrativeArea} city={city} zipCode={zipCode} country={country} currency={currency} financialEntity={financialEntity} iban={iban} bban={bban} bic={bic} language={language} qrType={qrType} companies={companies} setUuid={setUuid} setType={setType} setName={setName} setFirstName={setFirstName} setLastName={setLastName} setLogo={setLogo} setEmail={setEmail} setPhone={setPhone} setWebsite={setWebsite} setVatNumber={setVatNumber} setRegisterNumber={setRegisterNumber} setStreet={setStreet} setStreetNumber={setStreetNumber} setAdditional={setAdditional} setAdministrativeArea={setAdministrativeArea} setCity={setCity} setZipCode={setZipCode} setCountry={setCountry} setCurrency={setCurrency} setFinancialEntity={setFinancialEntity} setIban={setIban} setBban={setBban} setBic={setBic} setLanguage={setLanguage} setQrType={setQrType} setCompanies={setCompanies} submitContactCreate={submitContactCreate}></AppContactFormStepper>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default ContactCreatePage;
